import css from './GoodsAndServicesTransactions.module.scss'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import MediaQuery from 'react-responsive'
import { useParams } from 'react-router-dom'
import { useGet } from 'api'
import { EffectCoverflow } from 'swiper/modules'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { v4 as uuidv4 } from 'uuid'

import { BreakpointsMaxWidth } from 'meta/layout/breakpoints'
import {
  IGoodServiceTicketData,
  ISelectableGoodServiceTicket,
  Tap2GOStatus,
  Tap2GOTicketTicketStatus,
} from 'meta/pages/goodsandservices'
import { RouteParams } from 'meta/routes'
import { ApiEndpoint } from 'controller/endpoint'
import { Dates } from 'utils/dates'

import Head from 'components/common/Head'
import WithAppInitialized from 'components/common/WithAppInitialized'
import withTranslation, { useI18NContext } from 'components/HOC/WithTranslation'
import Modal from 'components/Layout/Modal'
import Img from 'components/Media/Img'
import { useIsAppInitialized } from 'hooks/useApp'
import { useBackendApiBase } from 'hooks/useEnv'
import { useOnUpdate } from 'hooks/useOnUpdate'

import GSEventBox from '../GoodsAndServices/GSEventBox'
import GSEventBoxSkeleton from '../GoodsAndServices/GSEventBoxSkeleton'
import GSFullDescriptionModal from '../GoodsAndServices/GSFullDescriptionModal'
import GSHiddenImages from '../GoodsAndServices/GSHiddenImages'
import GSMultipleItemsModal from '../GoodsAndServices/GSMultipleItemsModal'
import GSQRCodeModal from '../GoodsAndServices/GSQRCodeModal'
import GSTicketCard from '../GoodsAndServices/GSTicketCard'
import GSTicketSkeleton from '../GoodsAndServices/GSTicketSkeleton'
import Tap2GoMultipleModal from '../GoodsAndServices/Tap2GOMultipleModal'
import SliderProgress from './SliderProgress'

const GoodsAndServicesTransactions: React.FC = () => {
  const { uuid } = useParams<RouteParams['GoodsAndServicesTransactions']>()

  const [showTap2GoModal, setShowTap2GoModal] = useState<boolean>(false)
  const [showQRCodeModal, setShowQRCodeModal] = useState<boolean>(false)
  const [showMultipleItemsModal, setShowMultipleItemsModal] = useState<boolean>(false)
  const [initialUuid, setInitialUuid] = useState<string>('')
  const [showFullDescriptionModal, setShowFullDescriptionModal] = useState<boolean>(false)
  const [gsTransactionsData, setGsTransactionsData] = useState<IGoodServiceTicketData[] | null>(null)
  const [activeTicket, setActiveTicket] = useState<number>(0)
  const [activeCard, setActiveCard] = useState<number>(0)
  const [checkedAt, setCheckedAt] = useState<string | null>(null)

  const [tap2GoStatus, setTap2GoStatus] = useState<Tap2GOStatus>(Tap2GOStatus.request)

  const { namespacesLoaded } = useI18NContext()
  const backendApiBase = useBackendApiBase()
  const appInitialized = useIsAppInitialized()

  const { fetch, response } = useGet()

  const [selectedTicketIds, setSelectedTicketsIds] = useState<Array<number>>([])

  const swiperClassRef = useRef<SwiperClass>(null)

  useEffect(() => {
    if (!appInitialized || !backendApiBase || !uuid) return
    fetch({ urlResource: `${ApiEndpoint.notifications.tickets(uuid)}` })
  }, [appInitialized, backendApiBase, uuid])

  useOnUpdate(() => {
    if (response) {
      setGsTransactionsData(response.data?.tickets as IGoodServiceTicketData[])
      setInitialUuid(uuidv4())
    }
  }, [response])

  const handleTap2GoModalClose = () => {
    if (gsTransactionsData && checkedAt && tap2GoStatus === Tap2GOStatus.success) {
      if (selectedTicketIds.length > 0) {
        const updatedTransactionData: IGoodServiceTicketData[] = [...gsTransactionsData].map((elem, key) => {
          if (selectedTicketIds.includes(key)) {
            elem.dateEntry_tz = checkedAt
            elem.status = Tap2GOTicketTicketStatus.checked
          }

          return elem
        })

        setGsTransactionsData(updatedTransactionData)
      } else {
        const updatedTransactionData: IGoodServiceTicketData[] = [...gsTransactionsData]
        updatedTransactionData[activeTicket].dateEntry_tz = checkedAt
        updatedTransactionData[activeTicket].status = Tap2GOTicketTicketStatus.checked
        setGsTransactionsData(updatedTransactionData)
      }

      setCheckedAt(null)
    }

    setShowTap2GoModal(false)
    setTap2GoStatus(Tap2GOStatus.request)
    setSelectedTicketsIds([])
  }

  const handleSelectedTicketIds = (key: number, remove?: boolean) => {
    let updatedSelectedTicketIds: number[] = [...selectedTicketIds]

    if (remove) {
      updatedSelectedTicketIds = [...selectedTicketIds].filter((x) => x !== key)
    } else {
      updatedSelectedTicketIds.push(key)
    }

    setSelectedTicketsIds(updatedSelectedTicketIds)
  }

  const getUsableTickets = (): ISelectableGoodServiceTicket[] => {
    if (!gsTransactionsData) return []

    return gsTransactionsData
      .map((data, idx) => ({ idx, data }))
      .filter((ticket) => {
        const isEventEnded = Dates.isAfter(new Date(), new Date(ticket.data.validTo_tz))
        return ticket.data.status == Tap2GOTicketTicketStatus.valid && !isEventEnded && Boolean(ticket.data.secret)
      })
  }

  const getTicketCard = (data: IGoodServiceTicketData, key: number, insideSlider: boolean): ReactNode => {
    return (
      <GSTicketCard
        insideSlider={insideSlider}
        data={data}
        showTap2GoModal={() => {
          setActiveTicket(key)

          if (getUsableTickets().length > 1) {
            setShowMultipleItemsModal(true)
          } else {
            setShowTap2GoModal(true)
          }
        }}
        showQRCodeModal={() => {
          setActiveTicket(key)
          setShowQRCodeModal(true)
        }}
        showFullDescriptionModal={() => {
          setActiveTicket(key)
          setShowFullDescriptionModal(true)
        }}
      />
    )
  }

  return (
    <WithAppInitialized showChildren={namespacesLoaded}>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <div className={css.wrapper}>
        <div className={css.logo}>
          <Img alt="TicketSMS Logo" src="/logo/TicketSms_logo.svg" />
        </div>

        <GSHiddenImages />

        {gsTransactionsData && gsTransactionsData.length > 0 && (
          <>
            <GSEventBox data={gsTransactionsData[activeTicket]} />

            <MediaQuery maxWidth={BreakpointsMaxWidth.mobile}>
              <Swiper
                effect={'coverflow'}
                grabCursor={true}
                centeredSlides={true}
                slidesPerView={'auto'}
                coverflowEffect={{
                  rotate: 50,
                  stretch: 10,
                  depth: 300,
                  modifier: 1,
                  slideShadows: false,
                }}
                modules={[EffectCoverflow]}
                pagination={false}
                className={css.cards}
                onSlideChange={(swiper) => setActiveCard(swiper.activeIndex)}
                onAfterInit={(swiper) => {
                  //@ts-expect-error
                  swiperClassRef.current = swiper
                }}
              >
                {gsTransactionsData.map((gsTicketData, key) => {
                  return (
                    <SwiperSlide className={css.card} key={key}>
                      {getTicketCard(gsTicketData, key, true)}
                    </SwiperSlide>
                  )
                })}
              </Swiper>

              <SliderProgress
                activeCard={activeCard}
                totalCards={gsTransactionsData.length}
                setActiveCard={(cardNumber: number) => swiperClassRef?.current?.slideTo(cardNumber)}
              />
            </MediaQuery>

            <MediaQuery minWidth={BreakpointsMaxWidth.mobile}>
              {gsTransactionsData.map((gsTicketData, key) => {
                return (
                  <>
                    {getTicketCard(gsTicketData, key, false)}
                    <div className={css.spacer}></div>
                  </>
                )
              })}
            </MediaQuery>

            {showQRCodeModal && (
              <Modal
                closeIconPath="/icons/close_icon_white_smallcross.svg"
                transparent
                noBodyFixed
                show
                fullWidth
                limitDesktopWidth
                disableScroll
                handleClose={() => setShowQRCodeModal(false)}
              >
                <GSQRCodeModal data={gsTransactionsData[activeTicket]} />
              </Modal>
            )}

            {showFullDescriptionModal && (
              <Modal
                closeIconPath="/icons/close_icon_white_smallcross.svg"
                transparent
                noBodyFixed
                show
                fullWidth
                limitDesktopWidth
                disableScroll
                handleClose={() => setShowFullDescriptionModal(false)}
              >
                <GSFullDescriptionModal data={gsTransactionsData[activeTicket]} />
              </Modal>
            )}

            {showMultipleItemsModal && (
              <Modal
                show
                noBodyFixed
                hideCloseIcon
                fullWidth
                transparent
                limitDesktopWidth
                disableScroll
                overflowY
                handleClose={() => {
                  setShowMultipleItemsModal(false)
                  setSelectedTicketsIds([])
                }}
              >
                <GSMultipleItemsModal
                  activeCardData={{
                    idx: activeTicket,
                    data: gsTransactionsData[activeTicket],
                  }}
                  ticketsData={getUsableTickets()}
                  handleSelectedTicketData={(data: ISelectableGoodServiceTicket, remove?: boolean) => {
                    handleSelectedTicketIds(data.idx, remove)
                  }}
                  onClose={() => {
                    setShowMultipleItemsModal(false)
                    setSelectedTicketsIds([])
                  }}
                  onConfirm={() => {
                    setShowMultipleItemsModal(false)
                    setShowTap2GoModal(true)
                  }}
                  selectedTicketIds={selectedTicketIds}
                />
              </Modal>
            )}

            {showTap2GoModal && (
              <Modal
                show
                noBodyFixed
                hideCloseIcon
                fullWidth
                transparent
                limitDesktopWidth
                disableScroll
                overflowY
                handleClose={handleTap2GoModalClose}
              >
                <Tap2GoMultipleModal
                  initialUuid={initialUuid}
                  ticketsData={
                    selectedTicketIds.length > 0
                      ? gsTransactionsData.filter((elem, key) => selectedTicketIds.includes(key))
                      : [gsTransactionsData[activeTicket]]
                  }
                  setCheckedAt={(checkedAt: string) => setCheckedAt(checkedAt)}
                  onClose={handleTap2GoModalClose}
                  tap2GoStatus={tap2GoStatus}
                  setTap2GoStatus={setTap2GoStatus}
                />
              </Modal>
            )}
          </>
        )}

        {!gsTransactionsData && (
          <>
            <GSEventBoxSkeleton />

            <GSTicketSkeleton />
          </>
        )}
      </div>
    </WithAppInitialized>
  )
}

const namespaces = ['goodsAndServices']

export default withTranslation(namespaces)(GoodsAndServicesTransactions)
